import React, { useState, useMemo, useEffect } from 'react';
import { useSymbol } from '@/contexts/SymbolContext';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

interface SymbolOption {
  symbol: string;
  desc: string;
}

interface SymbolSelectorProps {
  selectedSymbol: string | null;
  onSymbolChange: (symbol: string | null) => void;
  style?: React.CSSProperties;
  size?: 'small' | 'medium';
  doNotInclude?: string[];
}

const SymbolSelector: React.FC<SymbolSelectorProps> = ({ selectedSymbol, onSymbolChange, style, size = 'small', doNotInclude }) => {
  const { contracts } = useSymbol();
  const [filterText, setFilterText] = useState(selectedSymbol || '');

  const nameToProductIdMap = useMemo(() => {
    return contracts.reduce(
      (map, contract) => {
        map[contract.productName] = contract.productId;
        return map;
      },
      {} as Record<string, string>
    );
  }, [contracts]);

  useEffect(() => {
    setFilterText(selectedSymbol || '');
  }, [selectedSymbol]);

  // Get unique symbols from contracts
  const symbols = useMemo(() => {
    doNotInclude = doNotInclude || [];
    return contracts
      .filter((contract) => !contract.disabled && !doNotInclude.includes(contract.productId))
      .reduce((acc: SymbolOption[], contract) => {
        if (!acc.some((item) => item.symbol === contract.productName)) {
          acc.push({ symbol: contract.productName, desc: contract.description });
        }
        return acc;
      }, [])
      .sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [contracts, doNotInclude]);

  // Find the currently selected option
  const selectedOption = useMemo(() => {
    return selectedSymbol ? symbols.find((option) => option.symbol === selectedSymbol) || null : null;
  }, [selectedSymbol, symbols]);

  return (
    <Box sx={{ margin: '1em 0.5em', display: 'flex', justifyContent: 'center', alignItems: 'center', ...style }}>
      <Autocomplete
        sx={{ flex: 2 }}
        value={selectedOption}
        inputValue={filterText}
        options={symbols}
        size={size}
        disableClearable
        filterOptions={(options, state) => {
          return options.filter((option) => option.symbol.toLowerCase().includes(state.inputValue.trim().toLowerCase()));
        }}
        getOptionLabel={(option) => option.symbol}
        noOptionsText={
          <Box>
            {symbols.length === 0 ? (
              <>
                <Typography>No Symbols Available</Typography>
                <Typography fontStyle='italic' fontSize='0.8em' color='grey'>
                  Check Data Subscription
                </Typography>
              </>
            ) : doNotInclude?.includes(nameToProductIdMap[filterText.trim()] || '') ? (
              <Typography sx={{ display: 'inline' }} fontWeight='bold' color={'error'}>
                {filterText.trim()} is already blocked
              </Typography>
            ) : (
              <Typography>
                No symbols found matching:
                <Typography sx={{ display: 'inline' }} fontStyle='italic' fontWeight='bold'>
                  {' ' + filterText.trim()}
                </Typography>
              </Typography>
            )}
          </Box>
        }
        onInputChange={(event, value, reason) => {
          if (reason !== 'reset') {
            setFilterText(value);
          }
        }}
        onBlur={() => {
          if (!filterText && selectedOption) {
            setFilterText(selectedOption.symbol);
          }
        }}
        onChange={(event, value) => {
          onSymbolChange(value ? value.symbol : null);
          setFilterText(value ? value.symbol : '');
        }}
        renderOption={(props, option) => (
          <Box component='li' {...props}>
            {option.symbol}
            <span style={{ color: 'grey', marginLeft: '1em' }}>({option.desc})</span>
          </Box>
        )}
        renderInput={(params) => <TextField label='Symbol' {...params} />}
      />
    </Box>
  );
};

export default React.memo(SymbolSelector);
